import { translateAdmin } from '../../../utils/adminTranslations';
import AdminText from '../AdminText';
import React, { useEffect, useRef, useState } from 'react';

import { boolElemByRole, intervalUpdatingData, showStatusBooking, showTypeBooking } from '../../../utils/dynamic';

import { fetchData, formatDate } from '../../../utils/api';
import { apiUrl } from '../../../config';
import BookingEdit from '../bookings/BookingEdit';
import BookingView from '../bookings/BookingView';
import { useLanguage } from '../../../LanguageContext';

const OffExpiredBookings = ({ setHeaderTitle }) => {
    React.useEffect(() => {
        setHeaderTitle('Не заселенные брони.');
    }, [setHeaderTitle]);
    const { language } = useLanguage();
    const [bookings, setBookings] = useState([])
    const [bookingsMessage, setBookingsMessage] = useState([])


    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const LIMIT = 30;


    const [isEditOpen, setIsEditOpen] = useState(false); // состояние для показа формы редактирования записи
    const [isViewOpen, setIsViewOpen] = useState(false); // состояние для просмотра записи
    const [editData, setEditData] = useState([]); // состояние с данными для редактирования записи
    const [viewData, setViewData] = useState([]); // состояние с данными для просмотра записи

    const [searchQuery, setSearchQuery] = useState(''); // состояние для поиска

    const [sortConfig, setSortConfig] = useState({ key: 'book_number', direction: 'desc' }); // состояние для сортировки по дате заезда/выезда

    const loadingRef = useRef(false);
    const hasMoreRef = useRef(true);
    const offsetRef = useRef(0);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        loadingRef.current = loading;
        hasMoreRef.current = hasMore;
        offsetRef.current = offset;
    }, [loading, hasMore, offset]);

    const handleBookingUpdate = (updatedBooking) => {
        setBookings(prev =>
            prev.map(b =>
                b.id === updatedBooking.id ? updatedBooking : b
            )
        );
    };

    // Handle VIEW/EDIT/DELETE click
    const handleVEDClick = (booking, action) => {
        switch (action) {
            case 'view':
                setViewData(booking)
                setIsViewOpen(true);
                break;
            case 'edit':
                setEditData(booking)
                setIsEditOpen(true);
                break;
            default:
                break
        }
    };
    // Handle VIEW/EDIT closer
    const handleVEClose = () => {
        setIsViewOpen(false);
        setIsEditOpen(false);
    };

    const fetchGAB = async (isInitial = false, customLimit = LIMIT, customOffset = 0, query = "") => {
        // Разрешаем выполнение, если это первая загрузка или если мы сейчас НЕ грузим что-то другое
        if (loadingRef.current && !isInitial) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            // Если customLimit не передан, берем стандартные 30
            const fetchLimit = customLimit || LIMIT;

            const data = await fetchData(
                `${apiUrl}/api/sys/off/get-bookings-expired?limit=${fetchLimit}&offset=${customOffset}&search=${encodeURIComponent(query)}`,
                "GET",
                token
            );

            if (data.request.status === 0) {
                const bData = data.request.data.booking || [];
                const roomsMap = new Map(data.request.data.rooms.map(room => [room.id, room]));

                const processedData = bData.map((booking) => {
                    const room = roomsMap.get(booking.room_id);
                    return {
                        ...booking,
                        room_type: room?.translations?.room_type?.[language] || '',
                        price: room?.price || '',
                    };
                });
                if (customOffset === 0) {
                    // СЛУЧАЙ 1: Обновление (интервал) или первая загрузка
                    // Просто заменяем данные. Если их было 60, и пришло 60 — список останется длинным.
                    setBookings(processedData);

                    // Если мы запрашивали стандартную пачку (30), проверяем, есть ли смысл листать дальше
                    if (fetchLimit === LIMIT) {
                        setHasMore(processedData.length === LIMIT);
                        setOffset(0); // Сбрасываем офсет, так как мы в начале
                    } else {
                        // Если мы обновляли, например, 60 записей, и пришло 60 — 
                        // мы не меняем hasMore, чтобы не сломать скролл
                        if (processedData.length < fetchLimit) {
                            setHasMore(false);
                        }
                    }
                } else {
                    // СЛУЧАЙ 2: Дозагрузка (скролл)
                    // Добавляем новые данные в хвост
                    setBookings(prev => {
                        // Берем только те записи, которых еще НЕТ в стейте
                        const newRecords = processedData.filter(
                            newItem => !prev.some(oldItem => oldItem.id === newItem.id)
                        );
                        return [...prev, ...newRecords];
                    });

                    // Если пришло меньше 30, значит база пуста
                    setHasMore(processedData.length === LIMIT);
                }
            }
        } catch (error) {
            console.error("Ошибка загрузки:", error);
        } finally {
            setLoading(false);
        }
    };

    // 3. Эффект инициализации и Интервала
    useEffect(() => {
        fetchGAB(true, 30, 0, searchQuery);

        const interval = setInterval(() => {
            // Если пользователь ничего не ищет — обновляем данные
            if (searchQuery === '') {
                fetchGAB(false, bookings.length, 0, '');
            }
        }, intervalUpdatingData + 27000);

        return () => clearInterval(interval);
    }, [searchQuery]);

    // 4. Исправленный обработчик скролла
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const isBottom = scrollTop + clientHeight >= scrollHeight - 100;

            if (isBottom && !loadingRef.current && hasMoreRef.current) {
                const nextOffset = offsetRef.current + LIMIT;
                setOffset(nextOffset);
                fetchGAB(false, LIMIT, nextOffset);
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);


    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchQuery(value);

        // При каждом изменении ищем через API
        fetchGAB(true, 30, 0, value);
    };

    const filteredBookings = bookings.filter((booking) =>
        (booking.id && booking.id.toString().includes(searchQuery)) ||
        (booking.room_type && booking.room_type.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (booking.cardholdername && booking.cardholdername.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (booking.cardholdername_old && booking.cardholdername_old.toLowerCase().includes(searchQuery.toLowerCase())) || // #tododel
        (booking.phone_number && booking.phone_number.includes(searchQuery)) ||
        (booking.available_from && booking.available_from.includes(searchQuery)) ||
        (booking.available_to && booking.available_to.includes(searchQuery)) ||
        (booking.book_number && booking.book_number.includes(searchQuery))
    );
    const handleSort = (key) => {
        let direction = 'asc';

        // Меняем направление, если сортируем по тому же полю
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedAndFilteredBookings = [...filteredBookings].sort((a, b) => {
        const key = sortConfig.key;
        const direction = sortConfig.direction === 'asc' ? 1 : -1;

        let valA = a[key];
        let valB = b[key];

        // Обработка дат
        if (['available_from', 'available_to'].includes(key)) {
            valA = new Date(formatDate(valA));
            valB = new Date(formatDate(valB));
        }

        // Обработка строк
        if (typeof valA === 'string' && typeof valB === 'string') {
            return valA.localeCompare(valB) * direction;
        }

        // Обработка чисел
        if (typeof valA === 'number' && typeof valB === 'number') {
            return (valA - valB) * direction;
        }

        // Обработка дат (уже как Date объекты)
        if (valA instanceof Date && valB instanceof Date) {
            return (valA - valB) * direction;
        }

        return 0;
    });

    // Функция для отображения стрелки
    const renderSortArrow = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'asc' ? '▲' : '▼';
        }
        return "▼"; // стрелка по умолчанию
    };

    return (
        <>
            {/* <Dashboard /> */}
            <section className="system-bookings-table">
                <h2 className='system-bookings-table__title'><AdminText>Бронирования добавленые в ручную</AdminText></h2>
                <h2 className='system-bookings-table__title'><AdminText>Не заселенные бронирования</AdminText></h2>
                <div className='d-flex just-content-sb'>
                    <input
                        type="text"
                        placeholder={translateAdmin("Поиск по тел/имени...")}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="search-input search-field"
                    />
                    {loading && (
                        <div className="loader">
                            <div className="circle"></div>
                            <div className="circle"></div>
                            <div className="circle"></div>
                            <div className="circle"></div>
                        </div>
                    )}
                </div>

                <div className='system-table' ref={scrollContainerRef} style={{ height: '70vh', overflowY: 'auto' }}>
                    <table className='system-table'>
                        <thead className='system-thead'>
                            <tr className='system-tr'>
                                <th className='system-th search-field'>№</th>
                                <th className='system-th search-field'><AdminText>Номер брони</AdminText></th>
                                <th className='system-th search-field'><AdminText>Номер бланка, чека или терминала</AdminText></th>
                                <th className='system-th search-field'><AdminText>Тип номера</AdminText></th>
                                <th className='system-th search-field'><AdminText>Имя плательщика</AdminText></th>
                                <th className='system-th search-field'><AdminText>Тип брони</AdminText></th>
                                <th className='system-th search-field'><AdminText>Тел.</AdminText></th>
                                <th className='system-th system-th__cursor' onClick={() => handleSort('available_from')}>Дата заезда {renderSortArrow('available_from')}</th>
                                <th className='system-th system-th__cursor' onClick={() => handleSort('available_to')}>Дата выезда {renderSortArrow('available_to')}</th>
                                <th className='system-th'><AdminText>Статус</AdminText></th>
                                <th className='system-th'><AdminText>Общая сумма</AdminText></th>
                                <th className='system-th'><AdminText>Отменено</AdminText></th>
                                {boolElemByRole(['admin', 'booker-admin']) && (
                                    <th className='system-th'><AdminText>Создал</AdminText></th>
                                )}
                                <th className='system-th'><AdminText>Действия</AdminText></th>
                            </tr>
                        </thead>
                        <tbody className='system-tbody'>

                            {sortedAndFilteredBookings && sortedAndFilteredBookings.length > 0 ? (
                                <React.Fragment>
                                    {sortedAndFilteredBookings.map((booking, index) => (
                                        <tr className="system-tr" key={index + 1}>
                                            <td className="system-td">{index + 1}</td>
                                            <td className="system-td">{booking.book_number}</td>
                                            <td className="system-td">{booking.blank_number ? booking.blank_number : booking.check_number_card ? booking.check_number_card : booking.check_number_cash ? booking.check_number_cash : ""}</td>
                                            <td className="system-td">{booking.room_type}</td>
                                            {booking.cardholdername ? (
                                                <td className="system-td">{booking.cardholdername}</td>
                                            ) : (
                                                <td className="system-td">{booking.cardholdername_old}</td>
                                            )}
                                            <td className="system-td">{showTypeBooking(booking.type)}</td>
                                            <td className="system-td">{booking.phone_number}</td>
                                            <td className="system-td">{booking.available_from}</td>
                                            <td className="system-td">{booking.available_to}</td>
                                            <td className="system-td">{showStatusBooking(booking.status)}</td>
                                            <td className="system-td">{booking.amount}</td>
                                            <td className="system-td">
                                                {booking.booking_status ? translateAdmin('Да') : translateAdmin('Нет')}
                                            </td>
                                            {boolElemByRole(['admin', 'booker-admin']) &&
                                                <td className="system-td">{booking.created_by_user_name}</td>
                                            }
                                            <td className="system-td">
                                                <button className="system-booking-table-btn system-booking-table-btn-view" onClick={() => handleVEDClick(booking, 'view')}><AdminText>Посмотреть</AdminText></button>
                                                {/* <button className="system-booking-table-btn system-booking-table-btn-view" onClick={() => handleVEDClick(booking, 'edit')}><AdminText>Изменить</AdminText></button> */}
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ) : (
                                <tr>
                                    <td className="system-td">{bookingsMessage}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    {loading && <p><AdminText>Загрузка данных...</AdminText></p>}
                    {!hasMore && bookings.length > 0 && <p style={{ color: 'gray' }}><AdminText>Все данные загружены</AdminText></p>}
                </div>
            </section>
            {isViewOpen && (
                <BookingView booking={viewData} onUpdate={handleBookingUpdate} onClose={handleVEClose} />
            )}
        </>
    );
};

export default OffExpiredBookings;